import { Grid } from '@factorialco/deprecated-design-system'
import {
  ContractDetailsOtherContractInformationDocument,
  ContractVersionPageContractVersionFragment,
} from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import Section from 'modules/contracts/components/ContractDetails/Section'
import BlankSection from 'modules/contracts/components/ContractDetails/Section/Blank'

import ContractObservations from './ContractObservations'
import CustomFields from './CustomFields'
import LoadingState from './LoadingState'

interface Props {
  employeeId: string
  contractVersion: ContractVersionPageContractVersionFragment
}

const OtherContractInformation = ({ employeeId, contractVersion }: Props) => {
  const { data, loading } = useQuery(ContractDetailsOtherContractInformationDocument, {
    variables: {
      employeeId: employeeId,
      contractVersionId: contractVersion.id ?? '0',
    },
  })
  if (loading) {
    return <LoadingState />
  }

  const customFieldsData = data?.companies.legalEntitiesConnection?.nodes[0]?.customFields ?? []
  const customFields = customFieldsData.map((node) => node.field).filter(Boolean) ?? []
  const contractVersionValues = data?.contracts.contractVersionsConnection?.nodes[0]?.values ?? []

  const contractObservations = contractVersion.esContractObservations ?? undefined
  const canEdit = contractVersion._policies?.edit?.can ?? false
  const hasCustomFields = customFields.length > 0
  const hasCustomFieldsData = contractVersionValues.length > 0

  const showEmptySlate =
    canEdit && !hasCustomFields && !hasCustomFieldsData && !contractObservations
  const showContractObservations =
    !showEmptySlate && contractVersion.country === 'es' && (canEdit || !!contractObservations)
  const showCustomFields = !showEmptySlate && hasCustomFields
  const showSection = showContractObservations || showCustomFields
  if (!showSection && !showEmptySlate) return <></>

  return (
    <Section icon="inbox" title={i18n.t('contract_versions.other_information.title')}>
      <Grid variant="fixed">
        {showCustomFields && (
          <CustomFields customFields={customFields} contractVersionValues={contractVersionValues} />
        )}
        {showContractObservations && <ContractObservations observations={contractObservations} />}
        {showEmptySlate && contractVersion.id && (
          <BlankSection
            employeeId={employeeId}
            contractVersionId={contractVersion.id}
            icon="inbox"
            title={i18n.t('contract_versions.other_information.blank_slate.title')}
            description={i18n.t('contract_versions.other_information.blank_slate.description')}
            buttonText={i18n.t('contract_versions.other_information.blank_slate.button')}
          />
        )}
      </Grid>
    </Section>
  )
}

export default observer(OtherContractInformation)
